import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";

import React, { useCallback, useMemo, useState } from "react";

import classNames from "classnames";
import format from "date-fns/format";
import isFuture from "date-fns/isFuture";
import parseJSON from "date-fns/parseJSON";
import truncate from "lodash/truncate";
import { twMerge } from "tailwind-merge";

import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons/faExternalLinkAlt";

import AddToCalendarButton from "components/AddToCalendarButton";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import RichContent from "components/RichContent";
import ShareButton from "components/ShareButton";
import useActivityTracker from "hooks/useActivityTracker";
import useMediaQuery from "hooks/useMediaQuery";
import { emberAppBaseUrl } from "utils/envVariables";
import htmlText from "utils/htmlToText";

import styles from "./style.module.scss";

const AudioPlayer = dynamic(() => import("components/AudioPlayer"), {
  ssr: false,
});

const AnnoucementSnippet = ({ item, useModal, isLatest }: any) => {
  const router = useRouter();
  const { mobile } = useMediaQuery();
  const { trackActivity } = useActivityTracker();

  const [showAccordionDetails, setShowAccordionDetails] = useState(false);

  const text = item.title;
  const firstUrl =
    item.attachment?.[0] || item.corporateAttachments?.[0]?.source;
  const releaseTime =
    item.releaseTime || item.publishedAt || item.startAt || item.createdAt;
  let detail = item.detail || item.bodyListicle;

  const isSingleUrl = useMemo(() => {
    if (item.type === "entity-news") {
      return (
        (item?.attachment?.length || 0) +
          (item?.corporateAttachments?.length || 0) ===
        0
      );
    }

    return (
      (item?.attachment?.length || 0) +
        (item?.corporateAttachments?.length || 0) ===
      1
    );
  }, [item?.attachment?.length, item?.corporateAttachments?.length, item.type]);

  if (detail) {
    detail = truncate(htmlText(detail), {
      length: 140,
      separator: /[/\- ]/,
    });
  }

  const url = useMemo(() => {
    if (item.type === "corporate-events") {
      return `/entities/${router.query.slug}/announcements?tab=events&item=${item.id}`;
    }
    if (item.type === "corporate-presentations") {
      return `/entities/${router.query.slug}/announcements?tab=presentations&item=${item.id}`;
    }
    if (item.type === "entity-earnings-alerts") {
      return `/entities/${router.query.slug}/announcements?tab=earnings-alerts&item=${item.id}`;
    }
    if (item.type === "exchange-announcements") {
      return `/entities/${router.query.slug}/announcements?item=${item.id}`;
    }
    if (item.type === "entity-news") {
      return item.url.startsWith("https://") ? item.url : `https://${item.url}`;
    }
    return "";
  }, [item.id, item.type, item.url, router.query.slug]);

  const type = useMemo(() => {
    if (item.type === "corporate-events") {
      return "Event";
    }
    if (item.type === "corporate-presentations") {
      return "Presentation";
    }
    if (item.type === "exchange-announcements") {
      return "Filing";
    }
    if (item.type === "entity-earnings-alerts") {
      return "Earnings";
    }
    if (item.type === "entity-news") {
      return "News";
    }
    return "";
  }, [item.type]);

  const isUpcomingQuarterEvent = useMemo(() => {
    return (
      item.type === "corporate-events" &&
      item.source === "quartr" &&
      (isFuture(parseJSON(releaseTime)) || !item.sourceUrl)
    );
  }, [item.source, item.sourceUrl, item.type, releaseTime]);

  const toggleDetails = useCallback(
    (event: React.MouseEvent, forceModal?: boolean) => {
      try {
        switch (item.type) {
          case "corporate-events":
            trackActivity("corporate_event.view", item);
            break;
          case "corporate-presentations":
            trackActivity("corporate_presentation.view", item);
            break;
          case "exchange-announcements":
            trackActivity("exchange_announcement.view", item);
            break;
          case "entity-news":
            trackActivity("entity_news.view", item);
            break;
        }
      } catch (err) {
        console.debug(err);
      }

      if (useModal || forceModal) {
        if (
          item.type === "corporate-events" ||
          item.type === "corporate-presentations" ||
          item.type === "entity-earnings-alerts"
        ) {
          event.preventDefault();
          return router.push(url, undefined, { shallow: true });
        }
        return;
      }
      if (!isUpcomingQuarterEvent) {
        if (
          item.type === "exchange-announcements" ||
          item.type === "corporate-presentations" ||
          item.type === "entity-news"
        ) {
          if (isSingleUrl) {
            event.preventDefault();
            if (item.type === "entity-news") {
              return window.open(url, "_blank");
            }
            return window.open(firstUrl, "_blank", "location=yes");
          }
        }
        setShowAccordionDetails(!showAccordionDetails);
      }
    },
    [
      useModal,
      isUpcomingQuarterEvent,
      item,
      trackActivity,
      router,
      url,
      showAccordionDetails,
      isSingleUrl,
      firstUrl,
    ]
  );

  const showDetail = useMemo(() => {
    if (item.source !== "quartr") return true;

    return !!item.sourceUrl;
  }, [item.source, item.sourceUrl]);

  const shareUrl = useMemo(() => {
    if (item.type === "corporate-events") {
      return `${emberAppBaseUrl}/entities/${router.query.slug}/announcements?tab=events&item=${item.id}`;
    }
    if (item.type === "corporate-presentations") {
      return `${emberAppBaseUrl}/entities/${router.query.slug}/announcements?tab=presentations&item=${item.id}`;
    }
    if (item.type === "entity-earnings-alerts") {
      return `${emberAppBaseUrl}/entities/${router.query.slug}/announcements?tab=earnings-alerts&item=${item.id}`;
    }
    if (item.type === "exchange-announcements") {
      return `${emberAppBaseUrl}/entities/${router.query.slug}/announcements?item=${item.id}`;
    }
    if (item.type === "entity-news") {
      return item.url.startsWith("https://") ? item.url : `https://${item.url}`;
    }
    return "";
  }, [item.id, item.type, item.url, router.query.slug]);

  const source = useMemo(() => {
    if (item.type === "corporate-events") {
      return "Company Event";
    }
    if (item.type === "corporate-presentations") {
      return "Company Presentation";
    }
    if (item.type === "exchange-announcements") {
      return "Exchange Announcement";
    }
    if (item.type === "entity-earnings-alerts") {
      return "Earnings";
    }
    if (item.type === "entity-news") {
      return "News";
    }
    return "";
  }, [item.type]);

  const clipboardText = `${item?.entity?.shortName} | ${item?.title}: ${shareUrl}`;
  const whatsappText = `Check this out ${clipboardText}`;
  const twitterText = truncate(item?.title, { length: 100 });
  const shareBtnOptions = {
    email: {
      link: `mailto:?subject=✪ Smartkarma | ${type}: | ${item?.title}&body=${whatsappText}`,
    },
    clipboard: {
      message: shareUrl,
    },
    privateMessage: {
      message: `Check this out <a href="${shareUrl}">${item?.entity?.shortName} | ${item?.title}</a>`,
    },
    whatsapp: {
      link: `https://web.whatsapp.com/send?text=${whatsappText}`,
    },
    twitter: {
      link: `https://twitter.com/share?url=${shareUrl}&text=${twitterText}&via=smartkarma`,
    },
    linkedIn: {
      link: `https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&source=Smartkarma`,
    },
  };

  return (
    <>
      <div
        className={classNames(styles.container, mobile && styles.mobileLayout)}
      >
        <div className="flex items-center justify-between group">
          <div className="flex-1 cursor-pointer group/title">
            {releaseTime && (
              <div
                className="flex items-center space-x-5 cursor-pointer"
                onClick={toggleDetails}
              >
                <div
                  className={twMerge(styles.time, "flex-1")}
                  suppressHydrationWarning
                >
                  <span>{source} • </span>
                  {format(parseJSON(releaseTime), "dd MMM yyyy HH:mm")}
                  {isUpcomingQuarterEvent && (
                    <>
                      {" "}
                      • Upcoming{"  "}
                      <AddToCalendarButton
                        startTime={parseJSON(releaseTime)}
                        endTime={parseJSON(releaseTime)}
                        location={item.location}
                        name={text}
                        details={htmlText(item.detail || item.content)}
                        onAddToCalendar={(type) => {
                          trackActivity(
                            "corporate_event.add_to_calendar",
                            item,
                            {
                              calendar: type,
                            }
                          );
                        }}
                      />
                    </>
                  )}
                  {isLatest && " • Latest"}
                </div>
                <ShareButton
                  shareKey={`entity-${item.type}-${item.id}`}
                  className="block md:hidden group-hover:block text-[13px] text-intelligent-silver hover:!text-smart-teal"
                  activeClassName="!block"
                  options={shareBtnOptions}
                  columnView
                  noBorder
                  noTooltip
                ></ShareButton>
              </div>
            )}
            <div className="flex flex-1 justify-between items-center mt-[8px]">
              <a
                className={twMerge(
                  styles.title,
                  "flex w-full",
                  useModal
                    ? "hover:text-smart-teal"
                    : "hover:text-active-black",
                  "group-hover/title:text-smart-teal"
                )}
                onClick={toggleDetails}
              >
                <h3 className="flex-1 mb-0 text-[18px] line-clamp-2">{text}</h3>
              </a>
            </div>
          </div>
        </div>
        <div
          className={twMerge(
            "px-0 transition-all duration-500",
            showAccordionDetails ? "mt-[16px]" : "hidden"
          )}
        >
          {item.type === "entity-news" && item.url && (
            <Link
              href={
                item.url.startsWith("https://")
                  ? item.url
                  : `https://${item.url}`
              }
              className="text-smart-teal group"
              target="_blank"
            >
              Link
              <FontAwesomeIcon
                icon={faExternalLinkAlt}
                className="ml-[5px] hidden group-hover:inline"
              />
            </Link>
          )}
          {item.type === "corporate-events" && (
            <>
              <div className={""}>
                <div className={""}>
                  {item.location && (
                    <div className={"text-[#555] mb-[10px]"}>
                      <strong>
                        <span>Location:</span>
                      </strong>
                      {item.location}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          {showDetail && (
            <div className="relative">
              {item.source === "quartr" ? (
                <div className="mt-[15px]">
                  <AudioPlayer url={item?.sourceUrl} title={item?.title} />
                  {item.quartrEventUrls?.reportUrl && (
                    <a
                      className="block text-smart-teal group cursor-pointer"
                      href="#"
                      onClick={() =>
                        window.open(
                          item.quartrEventUrls?.reportUrl,
                          "MyWindow",
                          "width=screen.availWidth,height=screen.availHeight"
                        )
                      }
                    >
                      Company Announcement
                      <FontAwesomeIcon
                        icon={faExternalLinkAlt}
                        className="ml-[5px] hidden group-hover:inline"
                      />
                    </a>
                  )}
                  {item.quartrEventUrls?.pdfUrl &&
                    item.quartrEventUrls?.pdfUrl !==
                      item.quartrEventUrls?.reportUrl && (
                      <a
                        className="block text-smart-teal group cursor-pointer"
                        onClick={() =>
                          window.open(
                            item.quartrEventUrls?.pdfUrl,
                            "MyWindow",
                            "width=screen.availWidth,height=screen.availHeight"
                          )
                        }
                      >
                        Company Presentation
                        <FontAwesomeIcon
                          icon={faExternalLinkAlt}
                          className="ml-[5px] hidden group-hover:inline"
                        />
                      </a>
                    )}
                </div>
              ) : (
                <RichContent
                  html={item.detail || item.bodyListicle}
                  className={`${item.type}-${item.source}-detail`}
                />
              )}
            </div>
          )}
          <div className="">
            {item.attachment?.map?.((url: string, index: number) => (
              <div key={url} className="text-smart-teal group">
                <a
                  className="cursor-pointer"
                  onClick={() =>
                    window.open(
                      url,
                      "MyWindow",
                      "width=screen.availWidth,height=screen.availHeight"
                    )
                  }
                >
                  Attachment {isSingleUrl ? "" : index + 1}
                  <FontAwesomeIcon
                    icon={faExternalLinkAlt}
                    className="hidden group-hover:inline"
                  />
                </a>
              </div>
            ))}
            {item.corporateAttachments?.map?.(
              (attachment: any, index: number) => {
                if (item.type === "corporate-presentations") {
                  return (
                    <div
                      key={attachment.id}
                      className="text-smart-teal cursor-pointer group"
                    >
                      <a
                        className="cursor-pointer"
                        onClick={(event) => toggleDetails(event, true)}
                      >
                        {attachment.name || "Attachment"}{" "}
                        {isSingleUrl ? "" : index + 1}
                        <FontAwesomeIcon
                          icon={faExternalLinkAlt}
                          className="hidden group-hover:inline"
                        />
                      </a>
                    </div>
                  );
                }

                return (
                  <div key={attachment.id} className="text-smart-teal group">
                    <a
                      className="cursor-pointer"
                      onClick={() =>
                        window.open(
                          attachment.source,
                          "MyWindow",
                          "width=screen.availWidth,height=screen.availHeight"
                        )
                      }
                    >
                      {attachment.name || "Attachment"}{" "}
                      {isSingleUrl ? "" : index + 1}
                      <FontAwesomeIcon
                        icon={faExternalLinkAlt}
                        className="hidden group-hover:inline"
                      />
                    </a>
                  </div>
                );
              }
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AnnoucementSnippet;
